'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { FC } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { CaretUp, ChevronRight } from '@eqi-investimentos/icons/web';
import { Button, Flex, Text } from '@eqi-investimentos/react';
import { Divider } from '@chakra-ui/react';
import { CardOpportunityProps } from '../../interface/secondaries';
import { PATHS } from '@/config/paths';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useCreateSFOpportunity } from '@/modules/Customer/http/client/mutations/useCreateSFOpportunity';
import { useGetCustomerAdvisor } from '@/modules/Customer/http/client/useGetCustomerAdvisor';
import { generateInfosLabelValue } from './utils';
import { sanitizeNumber } from '@/utils/format';
import { IfRender, MapRender } from '@/utils/jsx';
import { formatAdvisorWhatsAppLink } from '@/utils/messenger';
export const CardOpportinityProduct: FC<CardOpportinityProductProps> = ({
  data,
  handelViewDetails = () => {},
  isHub = false
}) => {
  const {
    t: i18n
  } = useTranslation('common');
  const router = useRouter();
  const {
    mutate: createSFOpportunity
  } = useCreateSFOpportunity();
  const {
    data: advisor
  } = useGetCustomerAdvisor();
  const mapInfos = generateInfosLabelValue(data, i18n);
  const handleSeeMore = () => {
    if (!isHub && data?.type === 'wallet') {
      router.push(`${PATHS?.hubs?.researchProgram}/${data?.program?.slug}`);
      return;
    }
    handelViewDetails(data);
  };
  const handleClickInvest = () => {
    const message = getMessageWhatsApp(advisor?.advisorName ?? '', {
      type: data?.type ?? '',
      productName: data?.subject ?? ''
    });
    createSFOpportunity({
      action: salesforceOpportunityConfig.investment.action,
      product: message
    });
    const url = formatAdvisorWhatsAppLink({
      message: message,
      number: sanitizeNumber(advisor?.advisorMobilePhone ?? '')
    });
    window.open(url, '_blank');
  };
  return <Flex direction="column" justifyContent="space-between" w="full" minH="21.25rem" p="l" gap="xs" borderRadius="xs" border="1px solid" borderColor="border.secondary" bg="background.primary" data-sentry-element="Flex" data-sentry-component="CardOpportinityProduct" data-sentry-source-file="index.tsx">
      {renderHeader(data)}

      <Divider my="xs" borderWidth="1px" borderColor="transparency.light" data-sentry-element="Divider" data-sentry-source-file="index.tsx" />

      <Flex direction="column" w="full" gap="s" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <MapRender items={mapInfos} render={({
        label,
        value,
        isPercentage,
        customProps
      }, idx) => <Flex key={idx} justifyContent="space-between" alignItems="center">
              <Text size="l" fontWeight="semibold" color="content.secondary">
                {label}
              </Text>

              <Text as="span" size="l" textAlign="right" fontWeight="normal" color="content.tertiary" {...customProps}>
                {value}
                <IfRender condition={!!isPercentage}>
                  <CaretUp boxSize="1rem" />
                </IfRender>
              </Text>
            </Flex>} data-sentry-element="MapRender" data-sentry-source-file="index.tsx" />
      </Flex>

      <Divider my="xs" borderWidth="1px" borderColor="transparency.light" data-sentry-element="Divider" data-sentry-source-file="index.tsx" />

      <Flex w="full" justifyContent="space-between" alignItems="center" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <Button w="9.375rem" h="2.5rem" p="s" onClick={handleClickInvest} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {i18n('attributes.invest')}
        </Button>

        <Button variant="naked" w="auto" h="auto" gap="2xs" onClick={handleSeeMore} rightIcon={<ChevronRight boxSize="1rem" color="content.brand" />} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          <Text size="l" color="content.brand" textTransform="capitalize" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            {i18n('attributes.details')}
          </Text>
        </Button>
      </Flex>
    </Flex>;
};
interface CardOpportinityProductProps {
  data: CardOpportunityProps;
  handelViewDetails?: (data: CardOpportunityProps) => void;
  isHub?: boolean;
}
const renderHeader = (data: CardOpportunityProps) => {
  const headerRenderers = {
    default: () => <Flex justifyContent="space-between" alignItems="center">
        <Text size="xl" maxW="16.25rem" color="content.primary" noOfLines={2}>
          {data?.subject}
        </Text>
        <Flex position="relative" w="2.5rem" h="2.5rem" bg="background.secondary" borderRadius="xs" overflow="hidden">
          <Image src={data?.thumb ?? ''} alt={data?.subject ?? 'logo produto investimento'} fill style={{
          objectFit: 'cover'
        }} onError={({
          currentTarget
        }) => {
          currentTarget.onerror = null;
          currentTarget.src = '/assets/placeholder_assets.png';
        }} />
        </Flex>
      </Flex>,
    cetipado: () => <Flex direction="column" justifyContent="center" alignItems="flex-start" gap="xs">
        <Text size="xl" color="content.primary">
          {data?.subject}
        </Text>
        <Text size="l" color="content.tertiary" noOfLines={2}>
          {data?.description}
        </Text>
      </Flex>,
    wallet: () => <Flex direction="column" justifyContent="center" alignItems="flex-start" h="5.625rem" gap="xs">
        <Flex w="full" minH="1.25rem" gap="2xs" flexWrap="wrap">
          <MapRender items={data?.categories ?? []} render={({
          name
        }, idx) => <Flex key={idx} justifyContent="center" alignItems="center" h="1.25rem" gap="2xs" bg="background.tertiary" px="xs" borderRadius="2xs">
                <Text size="m" fontWeight="semibold" color="content.secondary">
                  {name}
                </Text>
              </Flex>} />
        </Flex>
        <Flex direction="column" justifyContent="center" alignItems="flex-start" gap="xs">
          <Text size="xl" color="content.primary">
            {data?.subject}
          </Text>
          <Text size="l" color="content.tertiary" noOfLines={2}>
            {data?.program?.title ?? data?.description}
          </Text>
        </Flex>
      </Flex>
  };
  return headerRenderers[data?.type ?? 'default']();
};
const getMessageWhatsApp = (advisorName: string, {
  type,
  productName
}: {
  type: string;
  productName: string;
}) => {
  if (type === 'cetipado') {
    return `Olá ${advisorName.split(' ')[0]}, venho do EQI+ e gostaria de investir no produto ${productName}`;
  }
  if (type === 'wallet') {
    return `Olá ${advisorName.split(' ')[0]}, venho do EQI+ e gostaria de investir em um ou mais ativos da carteira ${productName}`;
  }
  return `Olá ${advisorName.split(' ')[0]}, venho do EQI+ e gostaria de investir no ativo ${productName}`;
};